import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Breadcrumbs, Link } from '@mui/material';
import { useDataLayerValue } from '../../contextAPI/DataLayer';
import { useLoading } from '../../hooks/useLoading';
import { useNotifications } from '../../hooks/useNotifications';
import {
  getDocumentRepositorySharedWithMe,
  getDocumentRepositorySubfolders,
  getDocumentRepositoryFolderFiles,
  getDocumentRepositoryFolderById,
  deleteDocumentRepositoryFile,
  deleteDocumentRepositoryFolder
} from '../../constants/requests';
import FileList from '../DocumentRepository/FileList';
import FolderList from '../DocumentRepository/FolderList';
import FileDetailsDialog from '../DocumentRepository/FileDetailsDialog';
import ConfirmDialog from '../DocumentRepository/ConfirmDialog';
import RenameFolderDialog from '../DocumentRepository/RenameFolderDialog';

/**
 * Shared Documents page.
 * Shows files and root folders shared with the current user.
 * Clicking a folder opens its contents with inherited access (view/edit).
 * View = open; Edit = open + delete (and rename for folders).
 */
export default function SharedDocuments() {
  const [user] = useDataLayerValue();
  const employeeId = user?._id ?? '';
  const { folderId: folderIdParam } = useParams();
  const navigate = useNavigate();
  const { request } = useLoading();
  const { showNotifications } = useNotifications();

  const currentFolderId =
    folderIdParam && folderIdParam.trim() !== '' ? folderIdParam : null;

  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [rootFolders, setRootFolders] = useState([]);
  const [rootFiles, setRootFiles] = useState([]);
  const [rootLoading, setRootLoading] = useState(true);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [filesLoading, setFilesLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [currentFolderName, setCurrentFolderName] = useState(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [fileDetailsOpen, setFileDetailsOpen] = useState(false);
  const [deleteFileOpen, setDeleteFileOpen] = useState(false);
  const [deletingFile, setDeletingFile] = useState(false);

  const [selectedFolder, setSelectedFolder] = useState(null);
  const [renameFolderOpen, setRenameFolderOpen] = useState(false);
  const [deleteFolderOpen, setDeleteFolderOpen] = useState(false);
  const [deletingFolder, setDeletingFolder] = useState(false);

  const loadSharedRoot = useCallback(async () => {
    if (!employeeId) return;
    setRootLoading(true);
    setLoadError(null);
    try {
      const response = await getDocumentRepositorySharedWithMe(employeeId);
      if (response?.data?.success && response?.data?.data) {
        const data = response.data.data;
        const fileList = Array.isArray(data.files) ? data.files : [];
        const folderList = Array.isArray(data.folders) ? data.folders : [];
        setRootFiles(fileList);
        setRootFolders(folderList);
      } else {
        setRootFiles([]);
        setRootFolders([]);
      }
    } catch (err) {
      setLoadError(
        err?.response?.data?.message || 'Failed to load shared documents.'
      );
      setRootFiles([]);
      setRootFolders([]);
    } finally {
      setRootLoading(false);
    }
  }, [employeeId]);

  const loadFolderContents = useCallback(
    async (folderId) => {
      if (!employeeId || !folderId) return;
      setFoldersLoading(true);
      setFilesLoading(true);
      try {
        const [folderRes, subfoldersRes, filesRes] = await Promise.all([
          getDocumentRepositoryFolderById(folderId, employeeId),
          getDocumentRepositorySubfolders(folderId, employeeId),
          getDocumentRepositoryFolderFiles(folderId, { employeeId }, employeeId)
        ]);
        if (folderRes?.data?.success && folderRes?.data?.data) {
          setCurrentFolderName(folderRes.data.data.name ?? 'Folder');
        }
        setFolders(
          subfoldersRes?.data?.success &&
            Array.isArray(subfoldersRes?.data?.data)
            ? subfoldersRes.data.data
            : []
        );
        setFiles(
          filesRes?.data?.success && Array.isArray(filesRes?.data?.data)
            ? filesRes.data.data
            : []
        );
      } catch (err) {
        showNotifications(
          'error',
          err?.response?.data?.message || 'Failed to load folder',
          4000
        );
        setFolders([]);
        setFiles([]);
        setCurrentFolderName(null);
      } finally {
        setFoldersLoading(false);
        setFilesLoading(false);
      }
    },
    [employeeId, showNotifications]
  );

  useEffect(() => {
    if (!employeeId) {
      setRootLoading(false);
      setLoadError('You must be logged in to view shared documents.');
      return;
    }
    loadSharedRoot();
  }, [employeeId, loadSharedRoot]);

  useEffect(() => {
    if (!employeeId) return;
    if (currentFolderId) {
      loadFolderContents(currentFolderId);
    } else {
      setFolders([]);
      setFiles([]);
      setCurrentFolderName(null);
    }
  }, [employeeId, currentFolderId, loadFolderContents]);

  const displayFolders = currentFolderId ? folders : rootFolders;
  const displayFiles = currentFolderId ? files : rootFiles;

  const refreshCurrent = useCallback(() => {
    if (currentFolderId) {
      loadFolderContents(currentFolderId);
    } else {
      loadSharedRoot();
    }
  }, [currentFolderId, loadFolderContents, loadSharedRoot]);

  const openFileDetails = (file) => {
    setSelectedFile(file);
    setFileDetailsOpen(true);
  };

  const openDeleteFile = (file) => {
    setSelectedFile(file);
    setDeleteFileOpen(true);
  };

  const confirmDeleteFile = async () => {
    const fileId = selectedFile?._id ?? selectedFile?.id;
    if (!fileId) return;
    setDeletingFile(true);
    try {
      const response = await request(() =>
        deleteDocumentRepositoryFile(fileId, employeeId)
      );
      if (response?.data?.success) {
        showNotifications('success', 'File deleted', 3000);
        setDeleteFileOpen(false);
        setSelectedFile(null);
        refreshCurrent();
      } else {
        showNotifications(
          'error',
          response?.data?.message ?? 'Failed to delete file',
          5000
        );
      }
    } finally {
      setDeletingFile(false);
    }
  };

  const onFolderClick = (id) => {
    navigate(`/shared-documents/folder/${id}`);
  };

  const openRenameFolder = (folder) => {
    setSelectedFolder(folder);
    setRenameFolderOpen(true);
  };

  const openDeleteFolder = (folder) => {
    setSelectedFolder(folder);
    setDeleteFolderOpen(true);
  };

  const confirmDeleteFolder = async () => {
    const folderId = selectedFolder?._id ?? selectedFolder?.id;
    if (!folderId) return;
    setDeletingFolder(true);
    try {
      const response = await request(() =>
        deleteDocumentRepositoryFolder(folderId, employeeId)
      );
      if (response?.data?.success) {
        showNotifications('success', 'Folder deleted', 3000);
        setDeleteFolderOpen(false);
        setSelectedFolder(null);
        if (currentFolderId === folderId) {
          navigate('/shared-documents');
        }
        refreshCurrent();
      } else {
        showNotifications(
          'error',
          response?.data?.message ?? 'Failed to delete folder',
          5000
        );
      }
    } finally {
      setDeletingFolder(false);
    }
  };

  if (!employeeId) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h5">Shared Documents</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          You must be logged in to view shared documents.
        </Typography>
      </Box>
    );
  }

  if (
    loadError &&
    !currentFolderId &&
    rootFolders.length === 0 &&
    rootFiles.length === 0
  ) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h5">Shared Documents</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          {loadError}
        </Typography>
        <Button
          variant="outlined"
          onClick={() => {
            setLoadError(null);
            loadSharedRoot();
          }}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Shared Documents
      </Typography>

      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/shared-documents')}
          sx={{ cursor: 'pointer', textDecoration: 'none' }}
        >
          Shared Documents
        </Link>
        {currentFolderId && currentFolderName && (
          <Typography variant="body2" color="text.primary">
            {currentFolderName}
          </Typography>
        )}
      </Breadcrumbs>

      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        Folders
      </Typography>
      <FolderList
        folders={displayFolders}
        loading={currentFolderId ? foldersLoading : rootLoading}
        onFolderClick={onFolderClick}
        onRenameFolder={openRenameFolder}
        onDeleteFolder={openDeleteFolder}
        sharedWithMeMode
      />

      <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{ mt: 2, mb: 1 }}
      >
        Files
      </Typography>
      <FileList
        files={displayFiles}
        loading={currentFolderId ? filesLoading : rootLoading}
        onViewFile={openFileDetails}
        onDeleteFile={openDeleteFile}
        sharedWithMeMode
        currentUserEmployeeId={employeeId}
      />

      <FileDetailsDialog
        open={fileDetailsOpen}
        fileId={selectedFile?._id ?? selectedFile?.id}
        employeeId={employeeId}
        onClose={() => {
          setFileDetailsOpen(false);
          setSelectedFile(null);
        }}
      />

      <ConfirmDialog
        open={deleteFileOpen}
        title="Delete file"
        description="Remove this file from the repository?"
        confirmText="Delete"
        loading={deletingFile}
        onClose={() => {
          if (!deletingFile) {
            setDeleteFileOpen(false);
            setSelectedFile(null);
          }
        }}
        onConfirm={confirmDeleteFile}
      />

      <RenameFolderDialog
        open={renameFolderOpen}
        folder={selectedFolder}
        employeeId={employeeId}
        onClose={() => {
          setRenameFolderOpen(false);
          setSelectedFolder(null);
        }}
        onSaved={refreshCurrent}
      />

      <ConfirmDialog
        open={deleteFolderOpen}
        title="Delete folder"
        description={`Delete "${
          selectedFolder?.name ?? 'this folder'
        }"? This cannot be undone.`}
        confirmText="Delete"
        loading={deletingFolder}
        onClose={() => {
          if (!deletingFolder) {
            setDeleteFolderOpen(false);
            setSelectedFolder(null);
          }
        }}
        onConfirm={confirmDeleteFolder}
      />
    </Box>
  );
}
